import { apiGet, apiPost, apiDelete } from './client';
import type {
  OperationResult,
  PagedItemsResult,
  PrebuiltPrompt,
  PrebuiltPromptType,
} from './types';

// ai.PrebuiltPrompt — "starter prompts" used as quick-action chips in chat
// and as agent-instruction pinned templates. Server: PrebuiltPromptController.

export interface PrebuiltPromptSearchParams {
  search?: string;
  type?: PrebuiltPromptType;
  categoryId?: number;
  /** Only prompts pinned to this agent (the server reads the link table). */
  agentId?: number;
  pageNo?: number;
  pageSize?: number;
}

export async function searchPrebuiltPrompts(
  params: PrebuiltPromptSearchParams = {}
): Promise<PagedItemsResult<PrebuiltPrompt>> {
  const { search = '', type, categoryId, agentId, pageNo = 1, pageSize = 100 } = params;
  const wrapped = await apiGet<OperationResult<PagedItemsResult<PrebuiltPrompt>>>(
    '/api/PrebuiltPrompt/Search',
    {
      search,
      pageNo,
      pageSize,
      ...(type ? { type: typeAsNumber(type) } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(agentId ? { agentId } : {}),
    }
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load prebuilt prompts');
  }
  return wrapped.data;
}

export async function getPrebuiltPromptById(id: number): Promise<PrebuiltPrompt> {
  const wrapped = await apiGet<OperationResult<PrebuiltPrompt>>(`/api/PrebuiltPrompt/GetById/${id}`);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || `Prompt ${id} not found`);
  }
  return wrapped.data;
}

export async function addPrebuiltPrompt(model: Partial<PrebuiltPrompt>): Promise<PrebuiltPrompt> {
  const wrapped = await apiPost<OperationResult<PrebuiltPrompt>>('/api/PrebuiltPrompt/Add', model);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to create prompt');
  }
  return wrapped.data;
}

export async function updatePrebuiltPrompt(model: Partial<PrebuiltPrompt>): Promise<PrebuiltPrompt> {
  const wrapped = await apiPost<OperationResult<PrebuiltPrompt>>('/api/PrebuiltPrompt/Update', model);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to update prompt');
  }
  return wrapped.data;
}

export async function removePrebuiltPrompt(id: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult>(`/api/PrebuiltPrompt/Remove/${id}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to delete prompt');
}

// ── Prompts pinned to a specific agent ─────────────────────────────────────

export async function searchPromptsForAgent(
  agentId: number,
  pageSize = 200
): Promise<import('./types').AssistantAIPrebuiltPromptLink[]> {
  const wrapped = await apiGet<OperationResult<PagedItemsResult<import('./types').AssistantAIPrebuiltPromptLink>>>(
    '/api/AgentPrebuiltPrompt/Search',
    { assistantAIId: agentId, pageNo: 1, pageSize }
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load pinned prompts');
  }
  return wrapped.data.data ?? [];
}

export async function updatePinDisplayOrder(linkId: number, displayOrder: number): Promise<void> {
  const wrapped = await apiPost<OperationResult>(`/api/AgentPrebuiltPrompt/Update/${linkId}`, {
    displayOrder,
  });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to update order');
}

// ── Prompt ↔ Agent linkage ─────────────────────────────────────────────────

export interface PromptAgentLinkSummary {
  promptId: number;
  agentCount: number;
  agentNames: string[];
}

export async function getAgentLinkage(): Promise<PromptAgentLinkSummary[]> {
  const wrapped = await apiGet<OperationResult<PromptAgentLinkSummary[]>>(
    '/api/PrebuiltPrompt/AgentLinkage'
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load agent linkage');
  return wrapped.data ?? [];
}

export interface AgentForPrompt {
  id: number;        // link row id (used for delete)
  agentId: number;   // ai.AssistantAI.Id
  displayName?: string;
  a2AAgentName?: string;
  displayOrder: number;
}

export async function getAgentsForPrompt(promptId: number): Promise<AgentForPrompt[]> {
  const wrapped = await apiGet<OperationResult<AgentForPrompt[]>>(
    `/api/PrebuiltPrompt/AgentsForPrompt/${promptId}`
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load agents for prompt');
  return wrapped.data ?? [];
}

// Pin/unpin link table operations.
export interface PinPromptToAgentInput {
  assistantAIId: number;
  prebuiltPromptId: number;
  displayOrder?: number;
}

export async function pinPromptToAgent(input: PinPromptToAgentInput): Promise<void> {
  const wrapped = await apiPost<OperationResult>('/api/AgentPrebuiltPrompt/Add', {
    assistantAIId: input.assistantAIId,
    prebuiltPromptId: input.prebuiltPromptId,
    displayOrder: input.displayOrder ?? 0,
  });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to pin prompt');
}

export async function unpinPromptFromAgent(linkRowId: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult>(`/api/AgentPrebuiltPrompt/Remove/${linkRowId}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to unpin prompt');
}

// Server uses int enums for Type. Map TS string to backend integer.
function typeAsNumber(t: PrebuiltPromptType): number {
  return t === 'QUICK_START' ? 0 : 1;
}

export function typeFromValue(v: PrebuiltPromptType | number | undefined): PrebuiltPromptType {
  if (v === 'QUICK_START' || v === 0) return 'QUICK_START';
  return 'AGENT_INSTRUCTION';
}
